/**
 * Copyright Highway9 Networks Inc.
 */
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { RootState } from "..";
import { Notification, NotificationStatus, NotificationUpdate, NotificationSource } from "~/types/Notification";
import { enqueueSnackbar } from "notistack";
import { createNotificationToast, mapOperationStatus, NOTIFICATION_MAX_HISTORY } from "~/components/Notifications/utils";

const OPERATION_TOAST_EXCEPTIONS_TYPES = ["IMPORT_SITE"]; // Types that should not trigger pop-up toasts

type NotificationState = {
  data: Notification[];
  latestNotification: Notification | null;
  maxHistory: number;
};

const initialState: NotificationState = {
  data: [],
  latestNotification: null,
  maxHistory: NOTIFICATION_MAX_HISTORY, // Keep last MAX_HISTORY notifications for recent view
};


const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    bulkAddNotifications: (state, action: PayloadAction<Notification[]>) => {
      state.data = action.payload;
      if (state.data.length > state.maxHistory) {
        state.data = state.data.slice(0, state.maxHistory);
      }
    },
    /**
     * Add a new notification
     */
    addNotification: (state, action: PayloadAction<Notification>) => {
      state.data.unshift(action.payload);
      // Limit history
      if (state.data.length > state.maxHistory) {
        state.data = state.data.slice(0, state.maxHistory);
      }
      state.latestNotification = action.payload;
    },

    /**
     * Update an existing notification
     */
    updateNotification: (
      state,
      action: PayloadAction<{ id: string; updates: NotificationUpdate }>
    ) => {
      const notification = state.data.find((n) => n.id === action.payload.id);
      if (notification) {
        Object.assign(notification, action.payload.updates);
      }
    },

    /**
     * Update notification by operation ID
     */
    updateNotificationByOperationId: (
      state,
      action: PayloadAction<{ operationId: string; updates: NotificationUpdate }>
    ) => {
      const notification = state.data.find(
        (n) => n.operationId === action.payload.operationId
      );
      if (notification) {
        Object.assign(notification, action.payload.updates);
      }
    },

    /**
     * Remove a notification
     */
    removeNotification: (state, action: PayloadAction<string>) => {
      state.data = state.data.filter((n) => n.id !== action.payload);
    },

    setLatestNotification: (state, action: PayloadAction<Notification | null>) => {
      state.latestNotification = action.payload;
    },

    /**
     * Clear all completed notifications
     */
    clearCompleted: (state) => {
      state.data = state.data.filter(
        (n) => n.status !== 'completed' && n.status !== 'cancelled'
      );
    },

    /**
     * Clear all notifications
     */
    clearAll: (state) => {
      state.data = [];
    },

    markAsRead: (state, action: PayloadAction<string>) => {
      const notification = state.data.find((n) => n.id === action.payload);
      if (notification) {
        notification.read = true;
      }
    },

    markAllAsRead: (state) => {
      state.data.forEach((notification) => {
        notification.read = true;
      });
    },

    /**
     * Sync notification with operation data (called from websocket events)
     */
    syncWithOperation: (
      state,
      action: PayloadAction<{
        operationId: string;
        operationName?: string;
        source: string;
        status: string;
        statusMessage?: string;
        progress?: number;
        output?: any;
        input?: any;
        completionTime?: number;
      }>
    ) => {
      const notification = state.data.find((n) => n.operationId === action.payload.operationId);
      if (!notification) return;

      const { status, statusMessage, source, progress, output, input, completionTime, operationName } = action.payload;

      const newStatus = status ? mapOperationStatus(status) : notification.status;
      if (status) notification.status = newStatus;
      if (statusMessage) notification.subtitle = statusMessage;
      if (progress !== undefined) notification.progress = progress;
      if (source) notification.source = source as NotificationSource;
      if (output) {
        notification.metadata = {
          ...notification.metadata,
          output,
          input,
        };
      }

      // Calculate completedIn when status changes to completed/failed/cancelled
      if (status && (newStatus === 'completed' || newStatus === 'failed' || newStatus === 'cancelled')) {
        const completion = completionTime;
        const creation = notification.timestamp;
        if (completion && creation && completion > 0 && creation > 0) {
          notification.completedIn = Math.max(0, completion - creation);
        }
      }

      notification.read = false;

      const isExceptionType = operationName && OPERATION_TOAST_EXCEPTIONS_TYPES.includes(operationName);
      const isFinalStatus = newStatus === 'completed' || newStatus === 'failed' || newStatus === 'cancelled';
      const shouldShowToast = !isExceptionType || isFinalStatus;
      
      if (shouldShowToast) {
        const variant = notification.status === 'completed' ? 'success'
          : notification.status === 'failed' ? 'error'
            : 'info';

        enqueueSnackbar(createNotificationToast(notification), { variant });
      }

      state.latestNotification = notification;
    },
  },
});

export const notificationActions = notificationsSlice.actions;
export default notificationsSlice.reducer;

// Selectors

/**
 * Select all notifications
 */
export const selectAllNotifications = (state: RootState) => state.notifications.data;
export const selectLatestNotification = (state: RootState) => state.notifications.latestNotification;
/**
 * Select active notifications (in-progress, queued, failed)
 */
export const selectActiveNotifications = (state: RootState) =>
  state.notifications.data.filter(
    (n) => n.status === 'in-progress' || n.status === 'queued' || n.status === 'failed'
  );

/**
 * Select notification by ID
 */
export const selectNotificationById = (id: string) => (state: RootState) =>
  state.notifications.data.find((n) => n.id === id);

/**
 * Select notification by operation ID
 */
export const selectNotificationByOperationId = (operationId: string) => (state: RootState) =>
  state.notifications.data.find((n) => n.operationId === operationId);

/**
 * Select active notification count (for badge)
 */
export const selectActiveNotificationCount = (state: RootState) =>
  state.notifications.data.filter(
    (n) => n.status === 'in-progress' || n.status === 'queued' || n.status === 'failed'
  ).length;

export const selectUnreadNotificationCount = (state: RootState) =>
  state.notifications.data.filter(
    (n) => !n.read
  ).length;

/**
 * Select notifications by status
 */
export const selectNotificationsByStatus = (status: NotificationStatus) => (state: RootState) =>
  state.notifications.data.filter((n) => n.status === status);

/**
 * Select notifications by type
 */
export const selectNotificationsByType = (type: string) => (state: RootState) =>
  state.notifications.data.filter((n) => n.type === type);

